import { Icon } from '@iconify-icon/react';
import Link from 'next/link';

import PageLink from '@/utils/PageLink';

import { useScrollReveal } from './_hooks';

/* ── Final CTA ────────────────────────────────────────
   Two paths: scroll back up to the form, or self-serve.
   Bold serif headline + asymmetric layout. ────────── */

const DemoFinalCTA: React.FC = () => {
  const [ref, visible] = useScrollReveal(0.3);

  return (
    <section
      className="relative overflow-hidden bg-[#0a1024]"
      style={{
        backgroundImage:
          'radial-gradient(ellipse 80% 50% at 30% 100%, #003366 0%, transparent 60%), ' +
          'radial-gradient(ellipse 60% 40% at 100% 0%, #1e293b 0%, transparent 55%)',
      }}>
      {/* Texture */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,1) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />
      {/* Diagonal hairline */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-1/4 top-0 h-full w-1/2 origin-top-right"
        style={{
          background:
            'linear-gradient(105deg, transparent 48%, rgba(192,136,56,0.18) 50%, transparent 52%)',
        }}
      />

      <div
        ref={ref}
        className="relative mx-auto max-w-6xl px-4 py-24 transition-all duration-1000 sm:px-6 sm:py-32 lg:px-8"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
        }}>
        <div className="grid grid-cols-1 items-end gap-12 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3">
              <span className="inline-block h-px w-8 bg-white/30" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-white/50">
                One last thing
              </span>
            </div>

            <h2
              className="mt-7 text-[44px] leading-[0.98] tracking-tight text-white sm:text-[64px] lg:text-[80px]"
              style={{ fontFamily: 'Fraunces, serif', fontWeight: 400 }}>
              Twenty-five minutes.
              <br />
              <span
                style={{
                  fontStyle: 'italic',
                  fontWeight: 500,
                  fontVariationSettings: '"SOFT" 100, "opsz" 144',
                  color: '#f3e4c4',
                }}>
                One working agent.
              </span>
            </h2>

            <p className="mt-7 max-w-xl text-[17px] leading-relaxed text-white/70">
              Or skip the call entirely — start a 14-day trial with no credit card. Choose a paid
              plan afterward. Most teams have one running before lunch.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row lg:col-span-4 lg:flex-col">
            <Link
              href="#book-demo"
              className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-7 py-4 text-[14px] font-semibold text-[#0a1024] shadow-[0_10px_30px_-10px_rgba(255,255,255,0.4)] transition-all duration-200 hover:bg-[#f3e4c4] hover:shadow-[0_10px_40px_-10px_rgba(243,228,196,0.6)]">
              Book the live demo
              <Icon
                icon="tabler:arrow-up-right"
                className="text-[18px] transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </Link>
            <Link
              href={PageLink.login}
              className="group inline-flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/[0.04] px-7 py-4 text-[14px] font-semibold text-white backdrop-blur-sm transition-all duration-200 hover:border-white/30 hover:bg-white/[0.08]">
              Start a 14-day trial
              <Icon icon="solar:arrow-right-linear" className="text-[16px]" />
            </Link>
          </div>
        </div>

        {/* Footer trust line */}
        <div className="mt-20 flex flex-col gap-4 border-t border-white/[0.08] pt-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-white/40">
            <span className="flex items-center gap-1.5">
              <Icon icon="solar:shield-check-bold" className="text-emerald-400" />
              ISO/IEC 27001:2022 certified
            </span>
            <span className="flex items-center gap-1.5">
              <Icon icon="tabler:progress-check" className="text-emerald-400" />
              SOC 2 Type II audit in progress
            </span>
            <span className="flex items-center gap-1.5">
              <Icon icon="solar:lock-keyhole-bold" className="text-emerald-400" />
              GDPR · CCPA
            </span>
            <span className="flex items-center gap-1.5">
              <Icon icon="solar:server-square-bold" className="text-emerald-400" />
              Data residency · EU / US
            </span>
          </div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/30">
            Avg time-to-first-agent · 4 minutes
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoFinalCTA;
